import {Component, Input, forwardRef, OnInit, OnChanges, Output, EventEmitter, OnDestroy} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR, FormBuilder, FormControl, Validators, ValidatorFn, NG_VALIDATORS, Validator, AbstractControl, ValidationErrors} from '@angular/forms';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {Common} from '../../../util/Util';

type InputType = 'text' | 'number' | 'email' | 'password';

@Component({
  selector: 'app-element-input,greydom-element-input',
  templateUrl: './element-input.component.html',
  styleUrls: [],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ElementInputComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ElementInputComponent),
      multi: true
    }
  ]
})
export class ElementInputComponent implements ControlValueAccessor, Validator, OnInit, OnChanges, OnDestroy {
  readonly destroyed$: Subject<boolean> = new Subject<boolean>();
  readonly debounceTime = Common.DEBOUNCE_TIME;

  @Input() label = '';
  @Input() type: InputType = 'text';
  @Input() placeholder = '';
  @Input() hint = '';
  @Input() maxLength: number | null = null;
  @Input() minLength: number | null = null;
  @Input() required = false;
  @Input() disabled = false;
  @Input() includeDebounceTime = false;
  @Input() min: number | null = null;
  @Input() max: number | null = null;
  @Input() step: number | null = null;
  @Input() cssClasses: string[] = [];
  @Input() customValidators: ValidatorFn[] = [];
  @Input() customErrorMessages: { [key: string]: string } = {};
  @Input() markAsDirtyOnEdit = false;

  @Output() valueChange: EventEmitter<any> = new EventEmitter<any>();

  inputControl: FormControl;

  constructor(private fb: FormBuilder) {
    this.inputControl = this.fb.control('');
  }

  get showMaxLengthHint(): boolean {
    return this.maxLength !== null && (this.type === 'text' || this.type === 'password');
  }

  get classes(): string {
    return Array.isArray(this.cssClasses) ? this.cssClasses.join(' ') : '';
  }

  ngOnChanges(): void {
    this.setValidators();
  }

  ngOnInit(): void {
    this.setValidators();

    this.inputControl.statusChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        if (this.onChange) {
          this.onChange(this.inputControl.value);
        }
      });

    this.inputControl.valueChanges
      .pipe(
        debounceTime(this.includeDebounceTime ? this.debounceTime : 0),
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe(value => {
        this.onChange(value);
        this.valueChange.emit(value);
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  onInputChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.inputControl.setValue(value, {emitEvent: true});
    this.onTouched();
  }

  setValidators(): void {
    if (!this.inputControl) {
      return;
    }

    const validators: ValidatorFn[] = [];

    if (this.customValidators?.length) {
      validators.push(...this.customValidators);
    }

    if (this.required) {
      validators.push(Validators.required);
    }

    if (this.type === 'text' || this.type === 'password') {
      if (this.minLength) {
        validators.push(Validators.minLength(this.minLength));
      }
      if (this.maxLength) {
        validators.push(Validators.maxLength(this.maxLength));
      }
    }

    if (this.type === 'number') {
      if (this.min !== null) {
        validators.push(Validators.min(this.min));
      }
      if (this.max !== null) {
        validators.push(Validators.max(this.max));
      }
    }

    if (this.type === 'email') {
      validators.push(Validators.email);
    }

    this.inputControl.setValidators(validators);
    this.inputControl.updateValueAndValidity({emitEvent: false});
  }

  getErrorMessage(): string {
    if (this.inputControl.errors) {
      const firstError = Object.keys(this.inputControl.errors)[0];

      if (this.customErrorMessages && this.customErrorMessages[firstError]) {
        return this.customErrorMessages[firstError];
      }

      switch (firstError) {
        case 'required':
          return 'This field is required';
        case 'minlength':
          return `Minimum length is ${this.minLength}`;
        case 'maxlength':
          return `Maximum length is ${this.maxLength}`;
        case 'min':
          return `Minimum value is ${this.min}`;
        case 'max':
          return `Maximum value is ${this.max}`;
        case 'email':
          return 'Please enter a valid email address';
        default:
          return 'Invalid input';
      }
    }
    return '';
  }

  writeValue(value: any): void {
    if (this.inputControl) {
      this.inputControl.setValue(value, {emitEvent: false});
      this.markControlTouchedAndDirty()
    }
  }

  onBlur(): void {
    this.onTouched();
    this.markControlTouchedAndDirty();
  }

  onChange: (value: any) => void = () => {};

  onTouched: () => void = () => {};

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.inputControl.disable({emitEvent: false});
    }
    else {
      this.inputControl.enable({emitEvent: false});
      this.markControlTouchedAndDirty();
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return this.inputControl?.errors || null;
  }

  private markControlTouchedAndDirty() {
    if (this.markAsDirtyOnEdit) {
      this.inputControl.markAsTouched();
      this.inputControl.markAsDirty();
    }
    this.inputControl.updateValueAndValidity();
  }
}
